import { useRef, useState } from "react";
import { trpc } from "../../_core/trpc";
import { Panel } from "./Admin";
import { LIMITS, useUploader } from "./upload";
import { UploadStatusBar } from "./UploadStatus";
function AdminVideos() {
  const list = trpc.admin.listMedia.useQuery({ kind: "video" });
  const update = trpc.admin.updateMedia.useMutation();
  const remove = trpc.admin.deleteMedia.useMutation();
  const utils = trpc.useUtils();
  const { uploadOne, status, reset, busy } = useUploader("video");
  const fileRef = useRef(null);
  const [caption, setCaption] = useState("");
  const [errors, setErrors] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const videos = list.data ?? [];
  const refresh = async () => {
    await utils.admin.listMedia.invalidate();
    await utils.admin.overview.invalidate();
    await utils.public.state.invalidate();
  };
  const onFile = async (files) => {
    if (!files || files.length === 0) return;
    setErrors([]);
    for (const f of Array.from(files)) {
      const res = await uploadOne(f, { caption: caption.trim() || void 0 });
      if (!res.ok && res.message) setErrors((e) => [...e, `${f.name}: ${res.message}`]);
    }
    setCaption("");
    await refresh();
    if (fileRef.current) fileRef.current.value = "";
    reset();
  };
  return <Panel
    eyebrow="Video"
    title="Her film"
    description="MP4 or WebM, up to 100 MB. Portrait (9:16) and landscape both work; the player fits the clip without cropping. A poster frame is captured automatically from the video."
  >
      <div className="glass rounded-xl p-5">
        <div className="flex flex-col items-center gap-3 py-3 text-center">
          <div className="grid h-12 w-12 place-items-center rounded-full border border-[rgba(233,168,242,.3)]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 6h11v12H4zM15 10l5-3v10l-5-3z" stroke="#e9a8f2" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
          </div>
          <div>
            <p className="font-display text-lg text-[#f3f4f9]">Add the birthday video</p>
            <p className="mt-1 text-[0.72rem] text-[color:var(--muted-foreground)]">
              {LIMITS.video.label}
            </p>
          </div>
          <input
    className="field-lux max-w-sm"
    placeholder="Caption (optional)"
    value={caption}
    onChange={(e) => setCaption(e.target.value)}
    aria-label="Video caption"
  />
          <button
    type="button"
    onClick={() => fileRef.current?.click()}
    className="btn-gold !min-h-[42px] !text-[0.66rem]"
    disabled={busy}
  >
            {busy ? "Uploading\u2026" : "Choose video"}
          </button>
          <input
    ref={fileRef}
    type="file"
    accept={LIMITS.video.types.join(",")}
    multiple
    className="hidden"
    onChange={(e) => void onFile(e.target.files)}
  />
        </div>

        <div className="space-y-2">
          <UploadStatusBar status={status} />
          {errors.map((e, i) => <p key={i} role="alert" className="text-[0.75rem] text-[#ffc9cd]">
              {e}
            </p>)}
        </div>

        <p className="mt-3 rounded-lg border border-[rgba(233,168,242,.16)] bg-white/[0.02] px-3 py-2 text-[0.7rem] leading-relaxed text-[color:var(--muted-foreground)]">
          <strong className="text-[#dcdff0]">About large files:</strong> this site cannot compress
          video on the server. If a clip is too big, export it at 1080p (or 720p for a long clip) with
          H.264 — that usually brings a phone video under the limit.
        </p>
      </div>

      <div className="mt-5 space-y-4">
        {list.isLoading && <p className="text-sm text-[color:var(--muted-foreground)]">Loading videos…</p>}

        {!list.isLoading && videos.length === 0 && <div className="glass rounded-xl px-6 py-14 text-center">
            <p className="font-display text-lg text-[#f3f4f9]">No video yet</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-[color:var(--muted-foreground)]">
              Without a video the film section simply doesn&apos;t appear — the rest of her page works
              either way.
            </p>
          </div>}

        {videos.map((v) => <div key={v.id} className="glass overflow-hidden rounded-xl">
            <div className="grid gap-4 p-4 sm:grid-cols-[240px_1fr]">
              {
    /* In-panel preview: a real, seekable player. */
  }
              <div className="overflow-hidden rounded-lg bg-black">
                <video
    src={v.url}
    poster={v.posterUrl ?? void 0}
    controls
    playsInline
    preload="metadata"
    className="h-full max-h-[300px] w-full object-contain"
  />
              </div>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="pill">
                    {(v.sizeBytes / 1024 / 1024).toFixed(1)} MB
                  </span>
                  {v.durationSec ? <span className="pill">
                      {Math.floor(v.durationSec / 60)}:
                      {String(v.durationSec % 60).padStart(2, "0")}
                    </span> : null}
                  <span className="pill">{v.mime ?? "video"}</span>
                  {v.visibleBeforeUnlock && <span className="pill pill-locked">teaser</span>}
                </div>

                <input data-testid="video-caption"
    className="field-lux mt-3 !min-h-[38px] !text-sm"
    defaultValue={v.caption ?? ""}
    placeholder="Caption under the video"
    aria-label="Video caption"
    onBlur={async (e) => {
      const val = e.target.value.trim();
      if (val !== (v.caption ?? "")) {
        await update.mutateAsync({ id: v.id, caption: val || null });
        await refresh();
      }
    }}
  />

                <div className="mt-3 flex flex-wrap gap-2">
                  <button
    type="button"
    onClick={async () => {
      await update.mutateAsync({ id: v.id, visibleBeforeUnlock: !v.visibleBeforeUnlock });
      await refresh();
    }}
    className="btn-ghost-lux !min-h-[36px] !px-3 !text-[0.62rem]"
    aria-pressed={v.visibleBeforeUnlock}
  >
                    {v.visibleBeforeUnlock ? "Teaser: on" : "Show in teaser"}
                  </button>

                  {confirmId === v.id ? <div className="flex gap-1">
                      <button
    type="button"
    onClick={async () => {
      await remove.mutateAsync({ id: v.id });
      setConfirmId(null);
      await refresh();
    }}
    className="rounded-full bg-[#e11d48] px-3 py-1.5 text-[0.62rem] uppercase tracking-wider text-white"
  >
                        Delete permanently
                      </button>
                      <button
    type="button"
    onClick={() => setConfirmId(null)}
    className="rounded-full border border-white/15 px-3 py-1.5 text-[0.62rem] uppercase tracking-wider text-[color:var(--muted-foreground)]"
  >
                        Keep
                      </button>
                    </div> : <button
    type="button"
    onClick={() => setConfirmId(v.id)}
    className="btn-ghost-lux !min-h-[36px] !px-3 !text-[0.62rem]"
  >
                      Delete
                    </button>}
                </div>

                <p className="mt-3 text-[0.68rem] text-[color:var(--muted-foreground)]">
                  To replace it, upload a new clip and delete this one.
                </p>
              </div>
            </div>
          </div>)}
      </div>
    </Panel>;
}
export {
  AdminVideos as default
};
